import React from 'react';
import Select, { StylesConfig } from 'react-select';

export interface SelectOption {
    value: string;
    label: string;
}

interface Props {
    options: SelectOption[];
    value: string | null | undefined;
    onChange: (value: string) => void;
    placeholder?: string;
    /** id for the inner input (so a <label htmlFor> still focuses it). */
    inputId?: string;
    isDisabled?: boolean;
    /** Forwarded to a wrapper for cypress/testing parity with the old <select>. */
    dataCy?: string;
}

/**
 * A type-to-filter dropdown built on react-select, styled to match the app's
 * native form inputs. Drop-in replacement for a long `<select>` (timezone,
 * currency, …) where scrolling hundreds of options is painful. Menu is portaled
 * to <body> so it never gets clipped inside drawers/overflow containers.
 */
// Colors come from the --gs-* design tokens so the control follows light/dark
// theme automatically (the tokens flip under [data-theme="dark"]).
const styles: StylesConfig<SelectOption, false> = {
    control: (base, state) => ({
        ...base,
        minHeight: 44,
        borderRadius: 10,
        backgroundColor: 'var(--gs-surface, #fff)',
        borderColor: state.isFocused
            ? 'var(--gs-primary, #7266e8)'
            : 'var(--gs-border-strong, #d7d7e2)',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(114, 102, 232, 0.15)' : 'none',
        '&:hover': { borderColor: 'var(--gs-primary, #7266e8)' },
        fontSize: '0.95rem',
    }),
    menu: (base) => ({
        ...base,
        backgroundColor: 'var(--gs-surface, #fff)',
        border: '1px solid var(--gs-border, #ececf4)',
    }),
    menuList: (base) => ({ ...base, maxHeight: 280 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    singleValue: (base) => ({ ...base, color: 'var(--gs-text-body, #1d1a33)' }),
    input: (base) => ({ ...base, color: 'var(--gs-text-body, #1d1a33)' }),
    placeholder: (base) => ({ ...base, color: 'var(--gs-text-soft, #888)' }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? 'var(--gs-primary, #7266e8)'
            : state.isFocused
                ? 'var(--gs-primary-softer, #efedfd)'
                : 'var(--gs-surface, #fff)',
        color: state.isSelected ? '#fff' : 'var(--gs-text-body, #1d1a33)',
        cursor: 'pointer',
    }),
};

export default function SearchableSelect({
    options, value, onChange, placeholder = 'Selecione…', inputId, isDisabled, dataCy,
}: Props) {
    const selected = options.find((o) => o.value === value) ?? null;
    return (
        <div data-cy={dataCy}>
            <Select<SelectOption>
                inputId={inputId}
                classNamePrefix="gs-select"
                options={options}
                value={selected}
                onChange={(opt) => onChange(opt ? opt.value : '')}
                placeholder={placeholder}
                isSearchable
                isDisabled={isDisabled}
                noOptionsMessage={() => 'Nada encontrado'}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                styles={styles}
            />
        </div>
    );
}
